import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Filter, User } from '../models/user';
import { View } from '../models/view';
import * as Parse from 'parse';
import { ERROR_MSG } from '../shared/errorMessages';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  currentUser: BehaviorSubject<User> = new BehaviorSubject(null);
  views: BehaviorSubject<View[]> = new BehaviorSubject([]);
  userRoles: BehaviorSubject<any[]> = new BehaviorSubject([]);
  private roleViews: BehaviorSubject<any[]> = new BehaviorSubject([]);

  private roleViewsSubscription: Subscription = null;

  private liveQueryCurrentUser: Parse.LiveQuerySubscription = null;
  private liveQueryRoleViews: Parse.LiveQuerySubscription = null;
  private liveQueryViews: Parse.LiveQuerySubscription = null;

  private roleViewsCollection = Parse.Object.extend('RoleView');
  private viewsCollection = Parse.Object.extend('View');
  private deliveryMethodsCollection = Parse.Object.extend('DeliveryMethod');
  private paymentMethodsCollection = Parse.Object.extend('PaymentMethod');
  private platformsCollection = Parse.Object.extend('Platform');
  private filtersCollection = Parse.Object.extend('Filter');
  private userRolesCollection = Parse.User;
  private rolesCollection = Parse.Role;

  constructor() { }

  async login(email: string, password: string): Promise<any> {
    let user = await Parse.User.logIn(email, password);

    if (await this.userHasBranchRole(user)) {
      await this.startLiveQueryCurrentUser();
      return true;
    } else {
      // user does not have required role, return error
      this.logout();
      return Promise.reject({ code: 101, message: 'Invalid username/password.' });
    }
  }

  private async userHasBranchRole(user) {
    let query = new Parse.Query(this.rolesCollection);
    query.equalTo('users', user);
    query.include('project');

    let roles = (await query.findAll()).filter(r => r.get('project').get('name') == 'Despacho');

    if (roles.length > 0) {
      this.userRoles.next(roles);
      return true;
    } else {
      return false;
    }
  }

  logout(): Promise<any> {
    return Parse.User.logOut().then(async () => {
      if (this.currentUser.value) {
        this.currentUser.next(null);
        this.userRoles.next([]);
      }
      await this.stopLiveQueryCurrentUser();
      location.reload();
      return true;
    });
  }

  async stopLiveQueryCurrentUser(): Promise<void> {
    if (this.liveQueryCurrentUser) {
      this.liveQueryCurrentUser.unsubscribe();
    }
  }

  async startLiveQueryCurrentUser(): Promise<void> {
    try {
      if (this.liveQueryCurrentUser) {
        // stop live query to restart
        this.stopLiveQueryCurrentUser();
      }

      let user = await Parse.User.currentAsync();
      if (user) {
        await user.fetch();
        if (user.get('deleted') || !(await this.userHasBranchRole(user))) {
          this.logout();
        } else {
          let jsonUser = user.toJSON() as any;
          this.currentUser.next(jsonUser);

          let userQuery = new Parse.Query(this.userRolesCollection);
          userQuery.equalTo('objectId', user.id);

          this.liveQueryCurrentUser = await userQuery.subscribe();

          this.liveQueryCurrentUser.on('update', (user) => {
            if (user.get('deleted')) {
              this.logout();
            } else {
              let jsonUser = user.toJSON() as any;
              this.currentUser.next(jsonUser);
            }
          });

        }
      }
    } catch (error) {
      this.logout();
    }
  }

  async forgotPassword(email: string) {
    return Parse.Cloud.run('forgotPassword', { email: email }).then(result => {
      return result;
    })
  }

  async resetPassword(token: string, newPassword: string) {
    return Parse.Cloud.run('resetPassword', { token: token, newPassword: newPassword }).then(result => {
      return result;
    })
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return Parse.Cloud.run('changePassword', { password: currentPassword, newPassword: newPassword }).then(result => {
      return result;
    })
  }

  async stopLiveQueryViews(): Promise<void> {
    if (this.liveQueryViews) {
      this.liveQueryViews.unsubscribe();
    }
    if (this.liveQueryRoleViews) {
      this.liveQueryRoleViews.unsubscribe();
    }
    if (this.roleViewsSubscription) {
      this.roleViewsSubscription.unsubscribe();
    }
  }

  async startLiveQueryViews(): Promise<void> {
    let query = new Parse.Query(this.roleViewsCollection);
    query.containedIn('role', this.userRoles.value);

    this.liveQueryRoleViews = await query.subscribe();

    this.liveQueryRoleViews.on('open', async () => {
      this.roleViews.next(await query.findAll());
    });

    this.liveQueryRoleViews.on('enter', (roleView) => {
      let roleViews = this.roleViews.value;
      roleViews.push(roleView);
      this.roleViews.next(roleViews);
    });

    this.liveQueryRoleViews.on('leave', (roleView) => {
      let roleViews = this.roleViews.value;
      roleViews = roleViews.filter(r => r.id != roleView.id);
      this.roleViews.next(roleViews);
    });

    if (this.roleViewsSubscription) {
      await this.roleViewsSubscription.unsubscribe();
    }

    this.roleViewsSubscription = this.roleViews.subscribe(async (roleViews) => {
      if (this.liveQueryViews) {
        await this.liveQueryViews.unsubscribe();
      }

      let query = new Parse.Query(this.viewsCollection);
      let vv = roleViews.map(rv => rv.get('view').id);
      query.containedIn('objectId', vv);

      this.liveQueryViews = await query.subscribe();

      this.liveQueryViews.on('open', async () => {
        let views = (await query.findAll()).map((v) => {
          return v.toJSON() as any;
        });

        views.sort((a, b) => a.rankPosition - b.rankPosition);
        this.views.next(views);
      });

      this.liveQueryViews.on('enter', (view) => {
        let v = view.toJSON() as any;
        let found = false;
        let views = this.views.value;
        for (let vv of views) {
          if (vv.objectId == v.objectId) {
            found = true;
            break;
          }
        }
        if (!found) {
          views.push(v);
          views.sort((a, b) => a.rankPosition - b.rankPosition);
          this.views.next(views);
        }
      });


      this.liveQueryViews.on('leave', (view) => {
        let views = this.views.value;
        views = views.filter(v => v.objectId != view.id);
        this.views.next(views);
      });
    });
  }

  /**
   * get the filters of an user
   * @returns Return the filters of an user, and the optionsMap for each filter selection
   */
  async getFilters(): Promise<any> {
    let user = Parse.User.current();
    if (user) {
      let filtersQuery = new Parse.Query(this.filtersCollection);
      filtersQuery.equalTo('isAdmin', false);
      filtersQuery.containedIn('user', [user.id]);
      let filters = [];
      (await filtersQuery.findAll()).forEach(filter => {
        let filterObject = filter.toJSON();
        delete filterObject.user;
        filters.push(filterObject);
      });
      return { success: true, data: filters };
    }
    else {
      return { success: false, data: ERROR_MSG['USER_NOT_FOUND'] };
    }
  }

  /**
   * Save/Update a filter. to update, filter needs a objectId.
   * @param filter: Filter object to save or update
   * @returns {Promise} with the saved object if it was saved
   */
  async saveFilter(filter: Filter): Promise<any> {
    let currentUser = Parse.User.current();
    if (currentUser) {
      let newFilter = new this.filtersCollection();
      newFilter.set('user', currentUser);
      newFilter.set('options', filter.options);
      return newFilter.save().then(obj => {
        if (obj) {
          let filterObject = obj.toJSON();
          delete filterObject['user'];
          return { success: true, data: filterObject };
        }
        else {
          return { success: false, data: ERROR_MSG['FILTER_CANT_SAVE'] }
        }
      })
    }
    else {
      return { success: false, data: ERROR_MSG['USER_NOT_FOUND'] };
    }
  }

  async deleteFilter(filterId: string): Promise<any> {
    let filterQuery = new Parse.Query(this.filtersCollection);
    return filterQuery.get(filterId).then(filter => {
      if (filter) {
        return filter.destroy().then(destroyed => {
          if (destroyed) {
            let destroyedObject = destroyed.toJSON();
            delete destroyedObject['user'];
            return { success: true, data: destroyedObject };
          }
          else {
            return { success: false, data: ERROR_MSG['FILTER_CANT_DELETED'] };
          }
        })
      }
    }).catch(() => {
      return { success: false, data: ERROR_MSG['FILTER_NOT_FOUND'] };
    })
  }
}
